import { 
  signInWithRedirect, 
  signOut as amplifySignOut,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';

export type UserRole = 'Admin' | 'LicenceManager' | 'Viewer';

export interface AuthUser {
  userId: string;
  email: string;
  name?: string;
  role: UserRole | null;
}

/**
 * Initiates Google OAuth sign-in flow
 * Redirects user to Cognito hosted UI for Google authentication
 */
export async function signInWithGoogle(): Promise<void> {
  try {
    await signInWithRedirect({
      provider: 'Google',
    });
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw new Error('Failed to initiate Google sign-in');
  }
}

/**
 * Signs out the current user using Cognito hosted UI
 * This ensures proper redirect to the logout page
 */
export async function signOut(): Promise<void> {
  try {
    // First sign out from Amplify to clear local session
    await amplifySignOut();
    
    // Then redirect to Cognito logout URL to clear Cognito session
    const cognitoHostedUIUrl = import.meta.env.VITE_COGNITO_HOSTED_UI_URL || 'https://lms-248189901560.auth.us-east-1.amazoncognito.com';
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || '7nsk3g1fijvitn0c0do950peo4';
    const logoutUri = window.location.origin + '/logout';
    
    const logoutUrl = `${cognitoHostedUIUrl}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    
    // Redirect to Cognito logout URL
    window.location.href = logoutUrl;
  } catch (error) {
    console.error('Error signing out:', error);
    throw new Error('Failed to sign out');
  }
}

/**
 * Gets the currently authenticated user information
 * Returns null if no user is authenticated
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    const user = await amplifyGetCurrentUser();
    const session = await fetchAuthSession();
    
    // Extract user attributes from the session
    const idToken = session.tokens?.idToken;
    const email = idToken?.payload.email as string | undefined;
    const name = idToken?.payload.name as string | undefined;
    
    // Extract role from JWT claims
    const role = getUserRole(idToken?.payload);
    
    return {
      userId: user.userId,
      email: email || '',
      name,
      role,
    };
  } catch (error) {
    // User is not authenticated
    return null;
  }
}

/**
 * Extracts the user role from JWT token claims
 * Cognito stores group membership in the 'cognito:groups' claim
 * Returns the highest privilege role if user belongs to multiple groups
 */
export function getUserRole(tokenPayload: Record<string, any> | undefined): UserRole | null {
  if (!tokenPayload) {
    return null;
  }
  
  const groups = tokenPayload['cognito:groups'] as string[] | undefined;
  
  if (!groups || groups.length === 0) {
    return null;
  }
  
  // Return highest privilege role (Admin > LicenceManager > Viewer)
  if (groups.includes('Admin')) {
    return 'Admin';
  }
  
  if (groups.includes('LicenceManager')) {
    return 'LicenceManager';
  }
  
  if (groups.includes('Viewer')) {
    return 'Viewer';
  }
  
  return null;
}

/**
 * Checks if a user is authenticated
 * Returns true if user has a valid session
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    await amplifyGetCurrentUser();
    return true;
  } catch {
    return false;
  }
}
